import { useCallback, type ComponentType, type ReactNode } from "react";
import * as overlays from "@react-aria/overlays";
import { useStore } from "./store";

const PortalProvider =
	(overlays as { UNSAFE_PortalProvider?: ComponentType<{ getContainer: () => HTMLElement; children: ReactNode }> }).UNSAFE_PortalProvider ??
	null;

interface FullscreenModalPortalProviderProps {
	children: ReactNode;
}

/**
 * When the video is in fullscreen, modals portaled to document.body appear behind the fullscreen layer.
 * This provider makes React Aria overlays (modals, popovers) portal into the fullscreen element instead,
 * so they appear on top while staying in fullscreen.
 *
 * Requires @react-aria/overlays 3.22+ for UNSAFE_PortalProvider.
 */
export function FullscreenModalPortalProvider({ children }: FullscreenModalPortalProviderProps) {
	const isFullscreen = useStore((s) => s.isFullscreen);

	const getContainer = useCallback(() => {
		if (isFullscreen) {
			const container = document.getElementById("fullscreen-modal-portal");
			if (container) return container;
		}
		return document.body;
	}, [isFullscreen]);

	if (!PortalProvider) {
		return <>{children}</>;
	}

	return (
		<PortalProvider getContainer={getContainer}>
			{children}
		</PortalProvider>
	);
}
